/**
 * The Yeni Şablon wizard's own reference helpers — `m08d-wizard.js`, plus the factories it calls
 * in `m02-mockdb.js` (`makeField`, `makeCanvas`, `makeBackground`, `makeLayer`, `createTemplate`)
 * and the visibility contract in `m01c-validate.js`.
 *
 * They live here rather than in `reference.ts` because that file is shared by every screen and is
 * not to be edited while screens are being ported in parallel. What the wizard needs beyond it is
 * a mutable draft object with the reference's own field shape, the layout its seventh step builds
 * out of the chosen fields, and the DRAFT template record its last button writes.
 *
 * Nothing here publishes. The reference is explicit about it (`m08d-wizard.js` header): the wizard
 * creates a template, saves a draft and opens the editor; the single publish gate is the editor's.
 */
import constants from '../data/demo-constants.json';
import type { CertificateEmailDraft } from '../data/template-email';
import type { LinkedInCardDesign } from '../data/template-linkedin-card';
import type { DemoCanvas, DemoDraft, DemoFieldDefinition, DemoTemplate, SubjectType } from '../data/types';
import type { LibraryAsset } from './asset-library';
import { addMonths, fieldLabel, fmtDate, todayIso, trLower, trUpper } from './reference';
import {
  type Background,
  canonicalJSON,
  type Layer,
  type Placement,
  planToSVG,
  type RenderData,
  resolveImagePlacement,
  resolveRenderPlan,
  syncHashHex,
} from './render';
import { prototypeNowUTC } from './template-list';

// ------------------------------------------------------------------- vocabularies the wizard offers

export interface CanvasPreset {
  key: string;
  tr: string;
  width?: number;
  height?: number;
}

export interface FitModeOption {
  key: string;
  tr: string;
}

export interface CatalogField {
  key: string;
  tr: string;
  en: string;
  dataType: string;
  visibility?: Visibility;
  sample?: string;
  auto?: boolean;
  role?: string;
  required?: boolean;
  importable?: boolean;
  defaultValue?: string;
  normalization?: string[];
  validation?: Record<string, unknown>;
}

export const CANVAS_PRESETS = constants.CANVAS_PRESETS as CanvasPreset[];
export const FIT_MODES = constants.FIT_MODES as FitModeOption[];
export const FIELD_CATALOG = constants.FIELD_CATALOG as unknown as CatalogField[];

const RENDERER = constants.RENDERER;
const QR_ENGINE = constants.QR_ENGINE;
const LOCALE_PROFILE = constants.LOCALE_PROFILE;

export const WIZ_STEPS = [
  'Temel bilgiler',
  'Belge türü',
  'Alıcı ve izinler',
  'Tasarım',
  'Canvas ölçüsü',
  'Alanlar',
  'E-posta',
  'Önizleme',
];

// ------------------------------------------------------------------------- the visibility contract

export type Visibility = 'private' | 'public_metadata' | 'public_render' | 'public_metadata_and_render';

/** `m01c-validate.js:8` — the order is the order the wizard's «Görünürlük» select offers. */
export const VISIBILITY: Visibility[] = [
  'private',
  'public_metadata',
  'public_render',
  'public_metadata_and_render',
];

export const canRender = (v: string): boolean => v === 'public_render' || v === 'public_metadata_and_render';

/** System fields are private whatever a template asks for; the wizard shows the select disabled. */
const SYSTEM_PRIVATE_KEYS = [
  'email',
  'claim_token',
  'claim_token_hash',
  'internal_id',
  'batch_id',
  'admin_actor',
  'source_row_hash',
  'private_contact_email',
];
export const isSystemPrivate = (key: string): boolean => SYSTEM_PRIVATE_KEYS.includes(key);

/**
 * §2 of the reference: a recipient-specific image or signature claim no longer exists, so nothing
 * a field can be given is an image type. The predicate is kept because the layout builder and the
 * «Excel'den gelir» default both still ask the question.
 */
const REMOVED_DYNAMIC_DATA_TYPES = ['image', 'signature'];
export const isImageDataType = (t: string): boolean => REMOVED_DYNAMIC_DATA_TYPES.includes(t);
export const isGraphicDataType = (t: string): boolean => isImageDataType(t) || t === 'qr';

export const catalogField = (k: string): CatalogField | null =>
  FIELD_CATALOG.find((f) => f.key === k) ?? null;

// ------------------------------------------------------------------------------- small utilities

let uidCounter = 4000;
export const uid = (prefix: string): string => `${prefix}_${(++uidCounter).toString(36)}`;

/** `m01-utils.js:173` — Turkish letters are folded before the non-alphanumeric run is collapsed. */
export const slugify = (s: string): string =>
  trLower(s)
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');

// ------------------------------------------------------------------------------------ field factory

export interface WizardField {
  id: string;
  key: string;
  labels: { tr: string; en: string };
  dataType: string;
  required: boolean;
  importable: boolean;
  visibility: Visibility;
  defaultValue: string;
  normalization: string[];
  validation: Record<string, unknown>;
  sample: string;
  auto: boolean;
  role: string | null;
  custom: boolean;
}

export interface FieldOverrides {
  required?: boolean;
  importable?: boolean;
  visibility?: Visibility;
  auto?: boolean;
  custom?: boolean;
}

/** `m02-mockdb.js:82`. The three fail-closed rules at the end are the reference's, in its order. */
export function makeField(src: string | CatalogField, over: FieldOverrides = {}): WizardField {
  const base: CatalogField =
    typeof src === 'string'
      ? (catalogField(src) ?? { key: src, tr: src, en: src, dataType: 'string', sample: src })
      : src;
  const auto = over.auto !== undefined ? over.auto : !!base.auto;
  const key = base.key;
  const f: WizardField = {
    id: uid('fld'),
    key,
    labels: { tr: base.tr || key, en: base.en || base.tr || key },
    dataType: base.dataType || 'string',
    required: over.required ?? base.required ?? false,
    importable: over.importable ?? base.importable ?? !auto,
    visibility: over.visibility ?? base.visibility ?? 'public_metadata_and_render',
    defaultValue: base.defaultValue ?? '',
    normalization: base.normalization ?? [],
    validation: structuredClone(base.validation ?? {}),
    sample: base.sample ?? '',
    auto,
    role: base.role ?? null,
    custom: over.custom ?? catalogField(key) === null,
  };
  if (isSystemPrivate(key)) f.visibility = 'private';
  if (f.dataType === 'email') f.visibility = 'private';
  if (!VISIBILITY.includes(f.visibility)) f.visibility = 'private';
  return f;
}

export const wizHolderKey = (subjectType: string): string =>
  subjectType === 'organization' ? 'organization_name' : 'recipient_name';

/** The six fields a new template starts with; only the holder field is required by default. */
export function defaultWizardFields(subjectType: string): WizardField[] {
  const hk = wizHolderKey(subjectType);
  return [hk, 'credential_title', 'issue_date', 'expiry_date', 'credential_code', 'verification_qr'].map(
    (k) => makeField(k, { required: k === hk }),
  );
}

// --------------------------------------------------------------- canvas, background, layer factories

export function makeCanvas(o: { width?: number; height?: number }): DemoCanvas {
  const width = Math.max(16, Math.round(o.width || 1200));
  const height = Math.max(16, Math.round(o.height || 1200));
  return {
    width,
    height,
    aspectRatio: Number((width / height).toFixed(6)),
    orientation: width === height ? 'square' : width > height ? 'landscape' : 'portrait',
    unit: 'px',
    outputScale: 1,
  };
}

export interface AssetRefRecord {
  assetId: string;
  sha256: string;
}

export const assetRefOf = (a: LibraryAsset | null): AssetRefRecord | null =>
  a ? { assetId: a.id, sha256: a.sha256 } : null;

export interface WizardBackground extends Background {
  assetRef: AssetRefRecord | null;
  /** See `EditorBackground.hasAlpha`: recorded where the decoded asset is in hand. */
  hasAlpha?: boolean;
  fitMode: string;
  x: number;
  y: number;
  scale: number;
  crop: null;
  focal: null;
  rotation: number;
  opacity: number;
}

export function makeBackground(o: {
  assetRef?: AssetRefRecord | null;
  fitMode?: string;
  hasAlpha?: boolean;
}): WizardBackground {
  return {
    assetRef: o.assetRef ?? null,
    ...(o.hasAlpha === undefined ? {} : { hasAlpha: o.hasAlpha }),
    fitMode: o.fitMode || 'contain',
    x: 0.5,
    y: 0.5,
    scale: 1,
    crop: null,
    focal: null,
    rotation: 0,
    opacity: 1,
  };
}

type LayerSeed = Partial<Layer> & { kind: string };

/** `m02-mockdb.js:137` — the text defaults are the ones the editor later shows in its inspector. */
export function makeLayer(o: LayerSeed): Layer {
  const kind = o.kind;
  const base: Layer = {
    id: o.id || uid('lyr'),
    kind,
    fieldKey: o.fieldKey ?? '',
    x: o.x ?? 0.5,
    y: o.y ?? 0.5,
    width: o.width ?? 0.6,
    height: o.height ?? null,
    rotation: o.rotation ?? 0,
    zIndex: o.zIndex ?? 10,
    opacity: o.opacity ?? 1,
    hidden: false,
  };
  if (kind === 'text' || kind === 'static') {
    Object.assign(base, {
      text: o.text || '',
      font: o.font || 'Poppins',
      weight: o.weight ?? 600,
      style: 'normal',
      fontSizeRatio: o.fontSizeRatio ?? 0.04,
      minFontSizeRatio: o.minFontSizeRatio ?? 0.024,
      color: o.color || '#000000',
      align: o.align || 'center',
      verticalAlign: 'middle',
      lineHeight: 1.15,
      letterSpacing: 0,
      maxLines: o.maxLines ?? 1,
      fitMode: o.fitMode || 'shrink',
      textTransform: 'none',
    });
  }
  if (kind === 'qr') base.color = o.color || '#000000';
  if (kind === 'image') {
    base.assetRef = o.assetRef ?? null;
    base.imageFit = o.imageFit || 'contain';
    base.crop = null;
  }
  return base;
}

const textLayer = (fieldKey: string, o: Partial<Layer>): Layer => makeLayer({ ...o, kind: 'text', fieldKey });
const qrLayer = (o: Partial<Layer>): Layer =>
  makeLayer({ width: 0.08, ...o, kind: 'qr', fieldKey: 'verification_qr' });

// -------------------------------------------------------------------------------- the wizard draft

export type PreviewMode = 'short' | 'long' | 'org';

export interface WizardPerms {
  allowLinkedInProfile: boolean;
  allowLinkedInShare: boolean;
  allowDownload: boolean;
}

export interface WizardState {
  step: number;
  name: string;
  code: string;
  title: string;
  descTr: string;
  descEn: string;
  critTr: string;
  critEn: string;
  lang: 'tr' | 'en';
  validityMode: 'months' | 'none';
  validityMonths: number | null;
  credentialType: string;
  customTypeLabel: string;
  subjectType: SubjectType;
  publicationPolicy: string;
  perms: WizardPerms;
  asset: LibraryAsset | null;
  assetError: string | null;
  canvasPreset: string;
  canvasW: number;
  canvasH: number;
  aspectLock: boolean;
  fitMode: string;
  fields: WizardField[];
  customDraft: { label: string; key: string; type: string };
  preview: PreviewMode;
  built: WizardLayout | null;
  /**
   * A field the SERVER refused, routed back to the step that owns it.
   *
   * The wizard used to end a rejected create on step 7 with a toast — «code — biçimi geçersiz» —
   * and the field it was about four steps behind, unmarked. The seven steps' work is already kept
   * here; this is what lets the screen re-open the step, mark the input and say why, instead of
   * leaving the author to find it.
   */
  fieldError: { field: string; message: string } | null;
  /**
   * «Bu sertifikayla gönderilecek e-posta», carried until the certificate exists.
   *
   * The e-mail belongs to a template and the template is created by the LAST step, so what the
   * author writes here is held with the rest of the wizard's work and saved against the new
   * certificate immediately after it is created. Null means «use the standard message», which is
   * what a certificate that never visits this step gets.
   */
  emailDraft: CertificateEmailDraft | null;
  /**
   * A carried LinkedIn share arrangement, if one exists.
   *
   * The wizard has no LinkedIn step: the share image is arranged from the editor, after the
   * certificate exists, because a ninth chip in the stepper moved this screen's own layout. The
   * field survives the step's removal so that a wizard still holding an arrangement — this object
   * outlives a render — has it written to the new certificate rather than dropped. Null is the
   * normal value and means «use the automatic card», which is what every certificate gets.
   */
  linkedInCardDraft: LinkedInCardDesign | null;
}

/** `WIZ` at `m08d-wizard.js:8`, with `resetWizard`'s field seeding already applied. */
export function initialWizard(): WizardState {
  return {
    step: 1,
    name: '',
    code: '',
    title: '',
    descTr: '',
    descEn: '',
    critTr: '',
    critEn: '',
    lang: 'tr',
    validityMode: 'months',
    validityMonths: 12,
    credentialType: 'digital_badge',
    customTypeLabel: '',
    subjectType: 'person',
    publicationPolicy: 'require_recipient_acceptance',
    perms: { allowLinkedInProfile: true, allowLinkedInShare: true, allowDownload: true },
    asset: null,
    assetError: null,
    canvasPreset: 'native',
    canvasW: 1200,
    canvasH: 1200,
    aspectLock: true,
    fitMode: 'native',
    fields: defaultWizardFields('person'),
    customDraft: { label: '', key: '', type: 'text' },
    preview: 'short',
    built: null,
    fieldError: null,
    emailDraft: null,
    linkedInCardDraft: null,
  };
}

// ------------------------------------------------------------------------------ the built layout

export interface WizardLayout {
  canvas: DemoCanvas;
  background: WizardBackground;
  fieldDefinitions: WizardField[];
  layers: Layer[];
  safeAreas: unknown[];
}

/**
 * `buildWizardLayout` — starting layers for the chosen fields.
 *
 * The comment above it upstream is the point of it: no assumption about the document's type or
 * shape, only a vertical flow. The holder sits a little higher on a portrait canvas, at most three
 * secondary fields follow it, and the date, the code and the QR take the three fixed anchors.
 */
export function buildWizardLayout(w: WizardState): WizardLayout {
  const hk = wizHolderKey(w.subjectType);
  const W = w.canvasW;
  const H = w.canvasH;
  const portrait = H > W * 1.15;
  const layers: Layer[] = [];
  const textFields = w.fields.filter(
    (f) => !isGraphicDataType(f.dataType) && canRender(f.visibility) && f.key !== 'credential_title',
  );
  const secondary = textFields.filter(
    (f) => f.key !== hk && !['issue_date', 'expiry_date', 'credential_code'].includes(f.key),
  );
  const base = portrait ? 0.44 : 0.48;
  layers.push(
    textLayer(hk, {
      x: 0.5,
      y: base,
      width: 0.78,
      fontSizeRatio: portrait ? 0.036 : 0.05,
      minFontSizeRatio: portrait ? 0.02 : 0.028,
      color: '#111111',
      weight: 600,
      maxLines: 2,
      zIndex: 20,
    }),
  );
  secondary.slice(0, 3).forEach((f, i) => {
    layers.push(
      textLayer(f.key, {
        x: 0.5,
        y: base + 0.085 + i * 0.06,
        width: 0.66,
        fontSizeRatio: portrait ? 0.02 : 0.026,
        minFontSizeRatio: 0.014,
        weight: 400,
        color: '#7B756F',
        zIndex: 21 + i,
      }),
    );
  });
  if (w.fields.some((f) => f.key === 'issue_date'))
    layers.push(
      textLayer('issue_date', {
        x: 0.12,
        y: 0.9,
        width: 0.3,
        fontSizeRatio: 0.016,
        minFontSizeRatio: 0.011,
        weight: 500,
        color: '#333333',
        align: 'left',
        zIndex: 30,
      }),
    );
  if (w.fields.some((f) => f.key === 'credential_code'))
    layers.push(
      textLayer('credential_code', {
        x: 0.88,
        y: 0.9,
        width: 0.38,
        fontSizeRatio: 0.014,
        minFontSizeRatio: 0.01,
        weight: 500,
        color: '#333333',
        align: 'right',
        zIndex: 31,
      }),
    );
  if (w.fields.some((f) => f.key === 'verification_qr'))
    layers.push(qrLayer({ x: 0.5, y: 0.945, width: 0.05, zIndex: 32 }));
  w.fields
    .filter((f) => isImageDataType(f.dataType) && canRender(f.visibility))
    .forEach((f, i) => {
      layers.push(
        makeLayer({
          kind: 'image',
          fieldKey: f.key,
          x: 0.25 + i * 0.5,
          y: 0.78,
          width: 0.16,
          height: 0.08,
          fitMode: 'contain',
          zIndex: 33 + i,
        }),
      );
    });
  return {
    canvas: makeCanvas({ width: W, height: H }),
    background: makeBackground({
      assetRef: assetRefOf(w.asset),
      fitMode: w.fitMode,
      ...(w.asset ? { hasAlpha: w.asset.hasAlpha } : {}),
    }),
    fieldDefinitions: w.fields.map((f) => structuredClone(f)),
    layers,
    safeAreas: [],
  };
}

/** The fixed sample record step 7 draws with; the code is the reference's own literal. */
const PREVIEW_CODE = 'HPW-7K9N-4RXM-P2QD-W8TJ-5CYH';

export function wizPreviewData(w: WizardState, mode: PreviewMode, publicBase: string): RenderData {
  const hk = wizHolderKey(w.subjectType);
  const d: RenderData = {};
  for (const f of w.fields) d[f.key] = f.sample || fieldLabel(f, 'tr');
  d.credential_title = w.title || w.name;
  d.credential_code = PREVIEW_CODE;
  d.verification_url = `${publicBase}/c/${PREVIEW_CODE}`;
  d.issue_date = fmtDate(todayIso(), w.lang);
  d.expiry_date = w.validityMonths
    ? fmtDate(addMonths(todayIso(), w.validityMonths), w.lang)
    : w.lang === 'en'
      ? 'No expiration'
      : 'Süresiz';
  d[hk] =
    mode === 'long'
      ? w.subjectType === 'organization'
        ? 'Uluslararası Anadolu Lojistik ve Dağıtım Hizmetleri A.Ş.'
        : 'Alexandra Konstantinopoulou-Wetherington'
      : mode === 'org'
        ? 'ABC Holding'
        : w.subjectType === 'organization'
          ? 'ABC Holding'
          : 'Ayşe Yılmaz';
  return d;
}

// ---------------------------------------------------------------------------- preview with a new asset

/** `imageSVG` in `m04-engine.js`, which the ported engine keeps private. Same output, same order. */
function imageSVG(p: Placement, href: string, idSeed: string): string {
  const scaleX = p.dw / p.sw;
  const scaleY = p.dh / p.sh;
  const ix = p.dx - p.sx * scaleX;
  const iy = p.dy - p.sy * scaleY;
  const iw = p.sourceSize.width * scaleX;
  const ih = p.sourceSize.height * scaleY;
  let clipAttr = '';
  let defs = '';
  if (p.clip) {
    const id = `c_${syncHashHex(canonicalJSON({ seed: String(idSeed), placement: p }))}`;
    defs = `<clipPath id="${id}"><rect x="${p.clip.x}" y="${p.clip.y}" width="${p.clip.w}" height="${p.clip.h}"/></clipPath>`;
    clipAttr = ` clip-path="url(#${id})"`;
  }
  const img = `<image href="${href}" x="${ix.toFixed(3)}" y="${iy.toFixed(3)}" width="${iw.toFixed(3)}" height="${ih.toFixed(3)}" preserveAspectRatio="none"${p.opacity < 1 ? ` opacity="${p.opacity}"` : ''}${clipAttr}/>`;
  const body = p.rotation ? `<g transform="rotate(${p.rotation} ${p.pivot.x} ${p.pivot.y})">${img}</g>` : img;
  return (defs ? `<defs>${defs}</defs>` : '') + body;
}

/**
 * Steps 5 and 7 draw the wizard's layout with the uploaded design behind it.
 *
 * The render plan resolves a background through the content-addressed asset library, and that
 * library is sealed at build time from the reference's own dump — a file the reviewer picks in
 * this session is not in it, so `resolveAssetRef` would fail and the plan would record an
 * unresolved asset. The background is therefore resolved here, through the engine's own placement
 * solver and its own `<image>` shape, and injected over the plan's white underlay. Everything else
 * — geometry, shrink-to-fit, the QR — is the engine's.
 *
 * API GAP: a real backend stores the uploaded asset and returns an id the render plan can resolve,
 * at which point this whole function collapses back into `previewSVG`.
 */
export function wizardPreviewSVG(layout: WizardLayout, data: RenderData, asset: LibraryAsset | null): string {
  const W = layout.canvas.width;
  const H = layout.canvas.height;
  const plan = resolveRenderPlan({ ...layout, background: null }, data, { width: W, height: H });
  const svg = planToSVG(plan, { responsive: true, white: true });
  if (!asset) return svg;
  const bg = layout.background;
  const placement = resolveImagePlacement(asset, { x: 0, y: 0, w: W, h: H }, bg.fitMode, {
    x: bg.x,
    y: bg.y,
    scale: bg.scale,
    crop: bg.crop,
    focal: bg.focal,
    opacity: bg.opacity,
    rotation: bg.rotation,
  });
  const underlay = `<rect width="${W}" height="${H}" fill="#FFFFFF"/>`;
  const at = svg.indexOf(underlay);
  const cut = at >= 0 ? at + underlay.length : svg.indexOf('>') + 1;
  return `${svg.slice(0, cut)}\n${imageSVG(placement, asset.dataUrlForPrototype, `bg|${asset.id}`)}${svg.slice(cut)}`;
}

// ------------------------------------------------------------------------------- the draft template

const DEFAULT_ISSUER_STATEMENT_TR =
  'Bu kayıt, belgenin Happy Place to Work tarafından düzenlendiğini ve güncel durumunu doğrular; kişinin veya kuruluşun yetkinliğine ilişkin bir değerlendirme sonucu ya da bilimsel ölçüm iddiası taşımaz.';
const DEFAULT_ISSUER_STATEMENT_EN =
  'This record confirms that the credential was issued by Happy Place to Work and shows its current status; it is not an assessment result and makes no scientific measurement claim.';

/**
 * `createTemplate` + `makeDraft`, narrowed to what the wizard passes.
 *
 * The record opens as a DRAFT with no release and no `currentReleaseId`, which is the whole point:
 * an unpublished template cannot issue anything, and the editor's checklist is the only way out of
 * that state.
 */
export function createWizardTemplate(w: WizardState, layout: WizardLayout): DemoTemplate {
  const now = prototypeNowUTC();
  const subjectType = w.subjectType;
  const title = w.title || w.name;
  const draft: DemoDraft = {
    /* A new template names no LinkedIn organisation: the wizard has no field for it, and the
       issuer's own is the documented default until the editor's settings section sets one. */
    linkedInIssuer: null,
    /* And it seals ONE language — the one the wizard asked for. A second language is arranged in
       the editor, where the words and the artwork for it can actually be given. */
    localizedBackground: {},
    sealedLanguages: [],
    credentialType: w.credentialType,
    customTypeLabel: w.customTypeLabel || '',
    subjectType,
    holderField: wizHolderKey(subjectType),
    defaultLanguage: w.lang,
    localizedMetadata: {
      tr: {
        title: title || 'Yeni şablon',
        description: w.descTr,
        criteria: w.critTr,
        issuerStatement: DEFAULT_ISSUER_STATEMENT_TR,
      },
      en: {
        title: title || 'New template',
        description: w.descEn,
        criteria: w.critEn,
        issuerStatement: DEFAULT_ISSUER_STATEMENT_EN,
      },
    },
    validityPolicy: { mode: w.validityMonths ? 'months' : 'none', months: w.validityMonths },
    publicationPolicy: w.publicationPolicy,
    /* Rule 13 / PO v0.7: a kuruluş credential has no personal profile-add flow, so the permission
       is forced off here as well as in the step-3 UI — publish validation rejects a true. */
    permissions: {
      allowLinkedInProfile: subjectType === 'organization' ? false : w.perms.allowLinkedInProfile,
      allowLinkedInShare: w.perms.allowLinkedInShare,
      allowDownload: w.perms.allowDownload,
    },
    duplicatePolicy: {
      /*
       * The key, restricted to fields this template actually carries.
       *
       * `findDuplicate` reads every key field out of the row's CLAIMS and gives up on the first one
       * that is empty — «an empty key never matches anything». The second entry here was `email` for
       * a person, which is neither one of the six fields a new template starts with nor a claim at
       * all: the contact address travels as `recipientEmail`, an issuance input. So the duplicate
       * policy an administrator was shown could never fire, for any template this product created,
       * and the same person could be issued the same credential any number of times.
       *
       * Nothing else about the policy changes: same scope, same blocking states, same `skip`.
       */
      keyFields: [
        subjectType === 'organization' ? 'organization_name' : 'recipient_name',
        subjectType === 'organization' ? 'certification_period' : 'email',
      ].filter((k) => w.fields.some((f) => f.key === k)),
      scope: 'template',
      blockingStates: ['pending_acceptance', 'pending_admin_publish', 'active'],
      allowRenewalAfterExpiry: true,
      onDuplicate: 'skip',
    },
    emailDefaults: {
      lang: w.lang,
      subjectTemplate: '{{holder_name}} — {{credential_title}} hazır: onaylayın ve yayınlayın',
      bodyTemplate:
        'Merhaba {{holder_name}},\n\n{{credential_title}} kaydınızı hazırladık. Aşağıdaki bağlantıdan bilgileri kontrol edin; onayladığınızda kaydınız {{credential_url}} adresinde yayınlanır.\n\nOnaylamadan hiçbir bilginiz herkese açık hale gelmez.\n\nCredential ID: {{credential_code}}',
    },
    interoperability: {
      externalId: null,
      achievementType: null,
      criteriaUri: null,
      evidence: [],
      alignment: [],
      issuerProfileId: null,
      subjectIdentifiers: [],
      validFrom: null,
      validUntil: null,
      credentialStatus: null,
      schemaVersion: null,
      inLanguage: w.lang,
      imageAssetId: null,
    },
    canvas: layout.canvas,
    background: layout.background as unknown as Record<string, unknown>,
    fieldDefinitions: layout.fieldDefinitions as unknown as DemoFieldDefinition[],
    layers: layout.layers as unknown as Record<string, unknown>[],
    safeAreas: layout.safeAreas,
    renderProfile: {
      renderer: RENDERER,
      qrEngine: QR_ENGINE,
      localeProfile: { locale: w.lang, ...LOCALE_PROFILE },
      outputScale: 1,
      colorProfile: 'sRGB',
      fontAssurance: 'unknown',
    },
  };
  return {
    id: uid('tpl'),
    code: w.code || trUpper((w.name || 'ŞBL').split(/\s+/)[0] ?? 'ŞBL').slice(0, 8),
    name: w.name || 'Yeni şablon',
    status: 'draft',
    currentReleaseId: null,
    releases: [],
    draft,
    seeded: false,
    userModified: true,
    createdAt: now,
    updatedAt: now,
  };
}
